import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { TransactionItem } from '@/components/TransactionItem'
import { SkeletonListTemplate } from '@/components/SkeletonListTemplate'
import { Transaction } from '@/domain/transactions'

interface FriendTransactionsListProps {
  transactions: Transaction[]
  userId: string
  fullName: string
  skeletonActive?: boolean
  maxItems?: number
  showHeader?: boolean
}

export const FriendTransactionsList: React.FC<FriendTransactionsListProps> = ({
  transactions,
  userId,
  fullName,
  skeletonActive = false,
  maxItems = 3,
  showHeader = true,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleViewAllTransactions = () => {
    navigate('/user-transactions', {
      state: {
        userId,
        fullName,
      },
    })
  }

  if (skeletonActive) {
    return (
      <BaseView>
        <BaseView className="flex-row items-center justify-between mb-4">
          <BaseView className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <BaseView className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </BaseView>
        <SkeletonListTemplate sizeList={3} />
      </BaseView>
    )
  }

  const displayTransactions = transactions.slice(0, maxItems)

  if (transactions.length === 0) {
    return (
      <BaseView>
        <BaseText
          fontSize={18}
          fontFamily="semibold"
          color="label.default"
          className="mb-4"
        >
          {t('translation:transactions')}
        </BaseText>

        <BaseView className="items-center py-8">
          <BaseView
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{
              backgroundColor: 'var(--color-background-secondaryGray)',
              opacity: 0.3,
            }}
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: 'var(--color-icon-disabledInputIcon)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </BaseView>

          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.description"
            className="text-center"
          >
            {t('translation:noTransactionsYet')}
          </BaseText>

          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
            className="text-center mt-1"
            style={{ opacity: 0.7 }}
          >
            {t('translation:startTransactionWithUser', { name: fullName })}
          </BaseText>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView
      style={{
        backgroundColor: '#242426',
        borderRadius: 16,
        // padding: 16,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={handleViewAllTransactions}
    >
      {/* Header */}
      {showHeader && (
        <BaseView
          px={16}
          pt={12}
          data-cy="friend-transactions-list-header"
          className="flex-row items-start justify-between"
        >
          <BaseText
            fontSize={20}
            fontFamily="semibold"
            color="label.default"
            style={{ color: '#F8F8F8' }}
          >
            {t('translation:yourHistory')}
          </BaseText>
        </BaseView>
      )}

      {/* Transactions List */}
      <BaseView
        onClick={(e) => e.stopPropagation()}
        style={{
          pointerEvents: transactions.length > 3 ? 'none' : 'auto',
        }}
      >
        {displayTransactions.map((transaction, index) => (
          <TransactionItem
            key={`${transaction.transaction_id}-${index}`}
            transaction={transaction}
          />
        ))}
      </BaseView>
    </BaseView>
  )
}
